/* Moduli: campo, menu a tendina, gruppo apribile, selettore di strategia.
 *
 * I 59 campi di un tradebot non stanno in una colonna. Stanno in gruppi che si
 * aprono uno alla volta, e i parametri della strategia compaiono solo dopo la
 * scelta — con la descrizione accanto, non in un suggerimento al passaggio del
 * mouse. */

import { useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { IconaFreccia } from './Base';
import { CampoData } from './CampoData';

/* ---------- campo ---------- */

export interface PropsCampo extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  etichetta: string;
  /** Nota a destra dell'etichetta, es. "64 valori" o l'intervallo ammesso. */
  suggerimento?: string;
  unita?: string;
  /** Messaggio d'errore: appare SOTTO il campo, non in un riepilogo lontano. */
  errore?: string | null;
  testuale?: boolean;
}

export function Campo({
  etichetta,
  suggerimento,
  unita,
  errore,
  testuale = false,
  id,
  ...resto
}: PropsCampo) {
  const idAuto = useId();
  const idCampo = id ?? idAuto;

  /* Le date non passano da `<input type="date">`.
   *
   * Quel controllo prende il formato dalla LINGUA DEL BROWSER, non da `lang`
   * del documento: con Chrome in inglese il 5 settembre 2026 si legge
   * `09/05/2026`, cioè «9 maggio» per un italiano. Su un periodo di backtest
   * questo non è un dettaglio: sposta l'inizio e la fine della misura, e il
   * numero che esce è sbagliato senza sembrarlo.
   *
   * L'intercettazione sta QUI perché tutte e quattro le schermate con le date
   * passano da `Campo`: correggerle in un posto solo evita di rincorrerle una
   * per una, e di dimenticare la quinta. Il contratto non cambia — `value`
   * entra ISO e `onChange` esce ISO — quindi nessun chiamante va toccato. */
  const eData = resto.type === 'date';
  const [erroreData, setErroreData] = useState<string | null>(null);
  /* L'errore del chiamante viene prima: lui sa cose che il campo non sa, per
     esempio che questa data cade prima della prima candela disponibile. */
  const mostrato = errore || (eData ? erroreData : null);

  return (
    <div className="sb-campo">
      <label className="sb-campo__etichetta" htmlFor={idCampo}>
        <span className="sb-campo__nome">{etichetta}</span>
        {suggerimento && <span className="sb-campo__suggerimento">{suggerimento}</span>}
      </label>
      <div
        className={`sb-campo__guscio${eData ? ' sb-campo__guscio--data' : ''}${
          mostrato ? ' sb-campo__guscio--errore' : ''
        }`}
      >
        {eData ? (
          <CampoData
            id={idCampo}
            className={`sb-campo__input${testuale ? ' sb-campo__input--testo' : ''}`}
            aria-invalid={mostrato ? true : undefined}
            aria-describedby={mostrato ? `${idCampo}-errore` : undefined}
            etichetta={etichetta}
            onErrore={setErroreData}
            {...resto}
          />
        ) : (
          <input
            id={idCampo}
            className={`sb-campo__input${testuale ? ' sb-campo__input--testo' : ''}`}
            aria-invalid={mostrato ? true : undefined}
            aria-describedby={mostrato ? `${idCampo}-errore` : undefined}
            {...resto}
          />
        )}
        {unita && <span className="sb-campo__unita">{unita}</span>}
      </div>
      {mostrato && (
        <div className="sb-campo__errore" id={`${idCampo}-errore`}>
          {mostrato}
        </div>
      )}
    </div>
  );
}

/* ---------- menu a tendina nativo ---------- */

/**
 * Il menu di sistema, vestito col guscio dei campi.
 *
 * Nativo e non un elenco disegnato a mano: su telefono il selettore di sistema
 * è più grande del pollice, si apre a fondo schermo e funziona con la tastiera
 * e con le tecnologie assistive senza riscriverne il comportamento.
 *
 * Non sostituisce `CampoSelettore`, che apre un foglio ed è la forma giusta per
 * le scelte lunghe — una strategia fra 65. Questo è per le scelte corte: gli
 * exchange, la coppia di token di una catena.
 *
 * Sta qui e non in `pagine/wallet/` perché lo usano tre schermate — wallet,
 * account exchange e scarico dello storico — e due di esse lo importavano dai
 * file interni di una terza.
 */
export interface VoceSelettore {
  valore: string;
  etichetta: string;
  /** Spento con il motivo scritto accanto: un'opzione che sparisce si cerca a
   *  lungo, e chi la cerca non sa che è stata tolta apposta. */
  motivoSpento?: string;
}

export function Selettore({
  etichetta,
  suggerimento,
  valore,
  voci,
  onCambia,
  errore,
}: {
  etichetta: string;
  suggerimento?: string;
  valore: string;
  voci: VoceSelettore[];
  onCambia: (valore: string) => void;
  errore?: string | null;
}) {
  const id = useId();

  return (
    <div className="sb-campo">
      <label className="sb-campo__etichetta" htmlFor={id}>
        <span className="sb-campo__nome">{etichetta}</span>
        {suggerimento && <span className="sb-campo__suggerimento">{suggerimento}</span>}
      </label>
      <div className={`sb-campo__guscio${errore ? ' sb-campo__guscio--errore' : ''}`}>
        <select
          id={id}
          className="sb-campo__input sb-campo__input--testo sb-selettore"
          value={valore}
          onChange={(e) => onCambia(e.target.value)}
          aria-invalid={errore ? true : undefined}
        >
          {voci.map((v) => (
            <option key={v.valore} value={v.valore} disabled={Boolean(v.motivoSpento)}>
              {v.etichetta}
              {v.motivoSpento ? ` — ${v.motivoSpento}` : ''}
            </option>
          ))}
        </select>
      </div>
      {errore && <div className="sb-campo__errore">{errore}</div>}
    </div>
  );
}

/** Campo di sola lettura che apre un selettore: filtri, gruppo, coppia. */
export function CampoSelettore({
  etichetta,
  suggerimento,
  valore,
  attivo = false,
  onApri,
}: {
  etichetta: string;
  suggerimento?: string;
  valore: string;
  attivo?: boolean;
  onApri: () => void;
}) {
  return (
    <div className="sb-campo">
      <div className="sb-campo__etichetta">
        <span className="sb-campo__nome">{etichetta}</span>
        {suggerimento && <span className="sb-campo__suggerimento">{suggerimento}</span>}
      </div>
      <button
        type="button"
        onClick={onApri}
        className={`sb-campo__guscio${attivo ? ' sb-campo__guscio--attivo' : ''}`}
        style={{ width: '100%', cursor: 'pointer', textAlign: 'left' }}
      >
        <span
          className="sb-campo__input"
          style={{ color: attivo ? 'var(--sb-testo)' : 'var(--sb-testo-tenue)', lineHeight: 1.35 }}
        >
          {valore}
        </span>
        <IconaFreccia />
      </button>
    </div>
  );
}

/* ---------- gruppo apribile ---------- */

export function GruppoCampi({
  numero,
  titolo,
  conteggio,
  aperto,
  onApri,
  children,
}: {
  numero: number | string;
  titolo: string;
  /** Quanti campi contiene: dice il costo di aprirlo prima di aprirlo. */
  conteggio: string;
  aperto: boolean;
  onApri: () => void;
  children: ReactNode;
}) {
  const idCorpo = useId();
  return (
    <div className={`sb-gruppo${aperto ? ' sb-gruppo--aperto' : ''}`}>
      <button
        type="button"
        className="sb-gruppo__testata"
        onClick={onApri}
        aria-expanded={aperto}
        aria-controls={idCorpo}
      >
        <span className="sb-gruppo__numero">{numero}</span>
        <span className="sb-gruppo__titolo">{titolo}</span>
        <span className="sb-gruppo__conteggio">{conteggio}</span>
        <IconaFreccia verso={aperto ? 'su' : 'giu'} />
      </button>
      {aperto && (
        <div className="sb-gruppo__corpo" id={idCorpo}>
          {children}
        </div>
      )}
    </div>
  );
}

/* ---------- selettore di strategia ---------- */

export interface VoceStrategia {
  nome: string;
  /** Sigla mostrata nella tessera, es. "SEM". */
  sigla: string;
  numeroParametri: number;
  descrizione: string;
}

export function SelettoreStrategia({
  voci,
  scelta,
  onScegli,
}: {
  voci: VoceStrategia[];
  scelta: string | null;
  onScegli: (nome: string) => void;
}) {
  return (
    <div className="sb-colonna" style={{ gap: 'var(--sb-s-3)' }} role="radiogroup" aria-label="Strategia">
      {voci.map((v) => {
        const on = v.nome === scelta;
        return (
          <button
            key={v.nome}
            type="button"
            role="radio"
            aria-checked={on}
            className={`sb-strategia${on ? ' sb-strategia--scelta' : ''}`}
            onClick={() => onScegli(v.nome)}
          >
            <span
              className="sb-gruppo__numero"
              style={{
                width: 32,
                height: 32,
                borderRadius: 'var(--sb-r-3)',
                background: on ? 'var(--sb-accento-velo-attivo)' : 'var(--sb-superficie-3)',
                color: on ? 'var(--sb-accento-chiaro)' : 'var(--sb-testo-tenue)',
                fontSize: 'var(--sb-t-105)',
              }}
            >
              {v.sigla}
            </span>
            <span className="sb-strategia__corpo">
              <span className="sb-strategia__testata">
                <span className="sb-strategia__nome">{v.nome}</span>
                <span className="sb-distintivo sb-distintivo--contorno">
                  {v.numeroParametri} parametri
                </span>
              </span>
              <span className="sb-strategia__descrizione">{v.descrizione}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
